import { AlertTriangle, ImageOff, Minus, Plus, Trash2 } from 'lucide-react';
import { formatPaise } from '../../../../lib/format.js';
import type { CartItem } from '../types/cart.js';

export interface CartLineItemProps {
  item: CartItem;
  onQuantityChange: (item: CartItem, quantity: number) => void;
  onRemove: (item: CartItem) => void;
  isUpdating: boolean;
  isRemoving: boolean;
}

/** Availability copy for lines the server flags as `price_changed`/`quantity_reduced`/`unavailable` -- always driven by `item.availability`/`item.message`, never re-derived from price/stock fields client-side. */
function AvailabilityNotice({ item }: { item: CartItem }) {
  if (item.availability === 'unavailable') {
    return (
      <p className="mt-1 flex items-center gap-1.5 text-xs font-medium text-red-600">
        <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        {item.message ?? 'This item is no longer available.'}
      </p>
    );
  }
  if (item.availability === 'price_changed') {
    return (
      <p className="mt-1 flex items-center gap-1.5 text-xs font-medium text-amber-700">
        <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        Price has changed: {formatPaise(item.unitPriceSnapshotPaise)}
        {item.currentUnitPricePaise !== null && <> → {formatPaise(item.currentUnitPricePaise)}</>}
      </p>
    );
  }
  if (item.availability === 'quantity_reduced') {
    return (
      <p className="mt-1 flex items-center gap-1.5 text-xs font-medium text-amber-700">
        <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        {item.message ?? 'Available stock has dropped below the quantity in your cart.'}
      </p>
    );
  }
  return null;
}

export function CartLineItem({ item, onQuantityChange, onRemove, isUpdating, isRemoving }: CartLineItemProps) {
  const isUnavailable = item.availability === 'unavailable';

  const decrement = () => {
    if (item.quantity <= 1) {
      onRemove(item);
      return;
    }
    onQuantityChange(item, item.quantity - 1);
  };

  const increment = () => onQuantityChange(item, item.quantity + 1);

  return (
    <li className="flex gap-4 border-b border-gray-100 py-4 last:border-b-0">
      {/* No image URL is available from the cart DTO (`imageMediaAssetId` is a raw asset id, not resolvable here, and resolving it per-line would be an N+1 fetch) -- render a placeholder instead of fabricating a src. */}
      <div
        className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-gray-100 text-gray-300"
        aria-hidden="true"
      >
        <ImageOff className="h-6 w-6" />
      </div>

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-gray-900">{item.productNameSnapshot}</p>
        <p className="truncate text-xs text-gray-500">
          {item.variantNameSnapshot} · SKU {item.skuSnapshot}
        </p>
        <AvailabilityNotice item={item} />

        <div className="mt-3 flex flex-wrap items-center gap-3">
          <div className="inline-flex items-center rounded-full border border-gray-200">
            <button
              type="button"
              onClick={decrement}
              disabled={isUpdating || isRemoving}
              aria-label={item.quantity <= 1 ? 'Remove item' : 'Decrease quantity'}
              className="focus-visible:ring-brand flex h-8 w-8 items-center justify-center rounded-full text-gray-600 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Minus className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
            <span className="w-8 text-center text-sm font-semibold text-gray-900" aria-label={`Quantity ${item.quantity}`}>
              {item.quantity}
            </span>
            <button
              type="button"
              onClick={increment}
              disabled={isUpdating || isRemoving}
              aria-label="Increase quantity"
              className="focus-visible:ring-brand flex h-8 w-8 items-center justify-center rounded-full text-gray-600 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </div>

          <button
            type="button"
            onClick={() => onRemove(item)}
            disabled={isRemoving || isUpdating}
            className="focus-visible:ring-brand inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:underline focus-visible:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
            Remove
          </button>
        </div>
      </div>

      <div className="shrink-0 text-right">
        <p className={`text-sm font-semibold ${isUnavailable ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
          {formatPaise(item.lineSubtotalPaise)}
        </p>
      </div>
    </li>
  );
}
